import {
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { FinancingRequestDto } from 'tdc-web-backend/financing-requests/schemas';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../../../../../../components/modal/CustomModal';
import { secondaryBlue } from '../../../../../../../utils/color';
import { convertDate, formatDateFns } from '../../../../../../../utils/helpers';
import CustomButton from '../../../../../../../components/button/CustomButton';
import useGetMany from '../../../../../../../utils/hooks/crud-hooks/useGetMany';

interface IPreviousRequestModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  projectId: string | undefined;
  companyId: string | undefined;
}

const PreviousRequestModal = ({
  isOpen,
  setIsOpen,
  projectId,
  companyId,
}: IPreviousRequestModalProps) => {
  const { t, i18n } = useTranslation();
  const { data: previousRequests, isLoading: isLoadingPrevious } = useGetMany<FinancingRequestDto>({
    resource: `financing-requests?project=${projectId}&company=${companyId}`,
    enabled: isOpen && !!projectId && !!companyId,
  });

  const results = previousRequests?.data?.results ?? [];

  return (
    <CustomModal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      width="100%"
      sx={{ minWidth: '600px', maxWidth: '1050px' }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton
            sx={{
              borderRadius: '4px',
              bgcolor: 'secondaryBlue.100',
              width: '3rem',
              height: '3rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': { bgcolor: 'secondaryBlue.100' },
            }}
          >
            <AttachMoneyIcon sx={{ color: secondaryBlue[700] }} />
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {t('financing.previousRequests')}
          </Typography>
        </Stack>

        <Divider />

        {/* Table */}
        {isLoadingPrevious ? (
          <Typography variant="body2" color="primaryDark.400" textAlign="center" py={4}>
            {t('common.loading')}
          </Typography>
        ) : results.length === 0 ? (
          <Typography variant="body2" color="primaryDark.400" textAlign="center" py={4}>
            {t('financing.noPreviousRequests')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="heading4" color="primaryDark.400">
                      {t('common.date')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="heading4" color="primaryDark.400">
                      {t('common.contact')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="heading4" color="primaryDark.400">
                      {t('common.email')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="heading4" color="primaryDark.400">
                      {t('common.phone')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateFns(req.created, i18n.language).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {req.contactData.firstName} {req.contactData.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.contactData.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.contactData.phone || '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider />

        {/* Close button */}
        <Stack direction="row" justifyContent="flex-end">
          <CustomButton variant="secondary" onClick={() => setIsOpen(false)}>
            {t('common.close')}
          </CustomButton>
        </Stack>
      </Stack>
    </CustomModal>
  );
};

export default PreviousRequestModal;
